import { ArrowDropDown, ArrowDropUp } from "@mui/icons-material";
import { IconButton, Typography } from "@mui/material";
import { createContext, useContext } from "react";

import Locator from "@/locator";

/**
 * The membership whose role menu is open, read by each row's {@link RoleMenuButton}.
 *
 * It travels by context rather than through the column definitions: `flexRender` mounts each cell as a component, so
 * rebuilding the columns when the menu opens remounts every cell and detaches the button the menu is anchored to.
 */
export const OpenRoleMenuContext = createContext<string | undefined>(undefined);

type RoleMenuButtonProps = {
  /** The membership this button opens the role menu for. */
  membershipId: string;
  /** The role's display label. */
  label: string;
  /** The member's email or phone number, for the browser-test id. */
  memberTestId: string;
  /** Opens the role menu for `membershipId`, anchored to the clicked button. */
  onOpen: (anchor: HTMLElement, membershipId: string) => void;
};

/** The role cell's button on the members and invites tables; its arrow points up while its menu is open. */
export default function RoleMenuButton({ membershipId, label, memberTestId, onOpen }: RoleMenuButtonProps) {
  const openMembershipId = useContext(OpenRoleMenuContext);
  return (
    <IconButton
      onClick={(e) => onOpen(e.currentTarget, membershipId)}
      aria-label={Locator.workspaceNav.members.activeMembers.roleSelect}
      data-testid={Locator.workspaceNav.members.activeMembers.roleSelectTestId(memberTestId)}
      sx={{ textTransform: "capitalize", background: "transparent" }}
    >
      <Typography>{label}</Typography>
      {membershipId === openMembershipId ? <ArrowDropUp role="img" /> : <ArrowDropDown role="img" />}
    </IconButton>
  );
}
